import {
	type Action,
	type BoundActions,
	createActionsHook,
	type HookActionsFunction,
} from 'react-sweet-state';

import { type DocNode } from '@atlaskit/adf-schema/doc';
import type {
	RovoApp,
	SourceMode,
} from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import type { BrowserContextSanitizer } from '@atlassian/conversation-assistant-chat-prompt-input/types';
import { type FollowUpSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type { AdditionalAnalyticAttributes } from '@atlassian/conversation-assistant-message-actions-api/types';
import {
	type AgentVersionInput,
	type AlphaExtendedThinkingMode,
	type ConversationChannelScope,
	type Dialogues,
	type LastAgentMessageStatus,
	type MessageType,
	type RovoLabsConfirmedTools,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type AgentMetadata,
	type AgentVersion,
	type CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	type ConversationResultAttributionContext,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { chatStore } from './chatStore';
import { type ReconnectToInProgressStreamParams } from './reconnect-action';
import { type SendMessageConsumerPramas } from './sendMessageActionTypes';
import type {
	BulkResolveConversationActionsArgs,
	BulkResolveConversationUIActionsArgs,
	ChatStoreAction,
	ChatStoreContainerProps,
	FetchConversationArgs,
	FetchConversationChannelsDataArgs,
	MessageSource,
	PrefetchConversationMessagesArgs,
	ResolveConversationActionArgs,
	WarmConversationCacheArgs,
} from './types';

/**
 * This is the preferred hook if component does not need any state, as this will never trigger re-render
 */
export const useChatStoreActions: HookActionsFunction<
	BoundActions<
		ChatStorePrivateState,
		{
			abortRequests: (
				args_0?:
					| {
							conversationId?: string;
							deepResearchEnabled?: boolean;
							thinkDeeperEnabled?: boolean;
							fastModeEnabled?: boolean;
							taskModeEnabled?: boolean;
					  }
					| undefined,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addAgentMessage: (args_0: {
				agentNamedId: string;
				conversationId: string;
				content: string | DocNode;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			addErrorMessage: (args_0: {
				error: unknown;
				conversationId?: string;
				errorPrefix?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			answerNow: (args_0: {
				conversationId?: string;
				tmpMessageId: string; // For store operations (finding/updating the message)
				remoteMessageId: string; // For the API call
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			bulkResolveConversationActions: (
				args: BulkResolveConversationActionsArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			bulkResolveConversationUIActions: (
				args: BulkResolveConversationUIActionsArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			clearFollowUpMessages: (args_0: {
				conversationId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			stopTask: (args_0: {
				conversationId: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			clearTargetTaskMessageId: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
			createConversationOptimistically: (options: {
				name?: string;
				sourceId?: string;
				id?: string;
				timeCreated?: string;
				lastMessageTimestamp?: string;
				sourceAgentId?: string;
				status?: CHAT_STORE_STATUS;
				lastMessageType?: MessageType[];
				lastAgentMessageStatus?: LastAgentMessageStatus;
				agent?: AgentMetadata;
				agentVersion?: AgentVersion;
				resultAttributionContext?: ConversationResultAttributionContext;
				chatEntryPoint?: Conversation['chatEntryPoint'];
				rovoSpaceId?: string;
				scopes?: ConversationChannelScope[];
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			createSeededConversation: (options: {
				name?: string;
				dialogues: Dialogues[];
				sourceId?: string;
				id?: string;
				followUpSource: Omit<FollowUpSource, 'none'>;
				agentId?: string;
				agentVersion?: AgentVersion;
				resultAttributionContext?: ConversationResultAttributionContext;
				chatEntryPoint?: Conversation['chatEntryPoint'];
				tags?: string[];
				skipNextFetch?: boolean;
				// When the seeded conversation is scoped to a Rovo Space (e.g. created from
				// the SpaceLandingPage chat input or a space starter), set this so the
				// conversation appears under the space in the side nav and is excluded from
				// the regular conversation list immediately on optimistic creation.
				rovoSpaceId?: string;
				mode?: {
					deepResearchEnabled?: boolean;
					thinkDeeperEnabled?: boolean;
					fastModeEnabled?: boolean;
					taskModeEnabled?: boolean;
					webSearchEnabled?: boolean;
					sourceMode?: SourceMode;
					useCurrentPageContext?: boolean;
					appFilters?: RovoApp[];
				};
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<string | undefined>>;
			deleteConversation: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
			getChat: (
				args_0: FetchConversationArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			handleChatOpenPubsubAction: (args_0: {
				conversationId: string;
				agentId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			hydrateConversationWithExternalParams: (args_0: {
				autoSelectConversation: boolean;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			fetchConversationChannelsData: (
				args_0?: FetchConversationChannelsDataArgs | undefined,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			loadMoreConversationChannelsData: () => Action<
				ChatStorePrivateState,
				ChatStoreContainerProps,
				Promise<void>
			>;
			setConversationListMounted: (isConversationListMounted: boolean) => ChatStoreAction;
			setConversationData: ({
				conversationId,
				data,
			}: {
				conversationId: string;
				data: Partial<Conversation>;
			}) => ChatStoreAction;
			removeMessage: (args_0: {
				messageId: number;
				conversationId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			truncateFromLastHumanMessage: (args_0: {
				conversationId?: string;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<StoreChatItem[]>>;
			resolveConversationAction: (
				args: ResolveConversationActionArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
			sendMessage: (
				props: SendMessageConsumerPramas,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			sendMessageConfirmedTools: (args_0: {
				conversationId?: string;
				agentId?: string;
				confirmed_tools: RovoLabsConfirmedTools;
				messageSources?: Partial<MessageSource>;
				storeUserMessages?: boolean;
				webSearchEnabled?: boolean;
				deepResearchEnabled?: boolean;
				thinkDeeperEnabled?: boolean;
				alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
				fastModeEnabled?: boolean;
				taskModeEnabled?: boolean;
				includeAIFeatureContext?: boolean;
				agentVersion?: AgentVersionInput;
				actionedAnalyticsAttributes?: AdditionalAnalyticAttributes;
				resolutionType?: 'confirmed' | 'cancelled';
				productKey?: string;
				addCurrentPageAsContextUserPreference?: boolean;
				browserContextSanitizer?: BrowserContextSanitizer;
				hasStagedContent?: boolean;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			setSelectedConversation: (args_0: {
				conversationId: string;
				sourceAgentId?: string;
				/**
				 * When the id is not yet in `conversations` (e.g. opened from space-scoped GET list),
				 * seed the store from this payload so selection is not downgraded to empty chat.
				 */
				conversationMetadata?: Conversation & { isEmpty?: unknown; currentTask?: unknown };
				/**
				 * When set, ChatHistory will scroll to this specific message after the conversation loads.
				 */
				targetMessageId?: string | null;
			}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			updateConversation: ({
				conversationId,
				data,
			}: {
				conversationId: string;
				data: Partial<Pick<Conversation, 'name' | 'readStatus' | 'isPinned' | 'pinnedAt'>>;
			}) => ChatStoreAction;
			removeConversationFromStore: ({
				conversationId,
			}: {
				conversationId: string;
			}) => ChatStoreAction;
			reconnectToInProgressStream: (
				args_0: ReconnectToInProgressStreamParams,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			restoreTaskModeIfNeeded: () => ChatStoreAction;
			warmConversationCache: (
				args_0: WarmConversationCacheArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			prefetchConversationMessages: (
				args_0: PrefetchConversationMessagesArgs,
			) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
			seedConversation: ({
				conversationId,
				skipNextFetch,
			}: {
				conversationId: string;
				/** When true, ChatStoreContainer will skip its initial fetchConversation call.
				 * Use this when messages have already been populated locally (e.g. template flow)
				 * to prevent the fetch from overwriting locally-seeded messages. */
				skipNextFetch?: boolean;
			}) => ChatStoreAction;
		}
	>
> = createActionsHook(chatStore);
